import { useState } from 'react';

import { IconButtonText } from 'common/components/IconButtonText';
import withErrorBoundary from 'common/components/withErrorBoundary';
import { useTranslation } from 'common/hooks/useTranslation';
import { SfsUkPhoneNumberModal } from 'modules/cards/components/SfsUkPhoneNumberModal/SfsUkPhoneNumberModal';
import { getUserFactorsAndCheckIfPhoneActive } from 'modules/physical-cards/apis/getUserFactorsAndCheckIfPhoneActive';
import type { ApiCard } from 'src/core/modules/physical-cards/card';
import { AnalyticEventName, track } from 'src/core/utils/analytics';
import { logger } from 'src/utils/datadog-log-wrapper';

import { LazyRevealPinScaModal } from './LazyRevealPinScaModal';
import { useSFSRevealPinAccess } from './useSFSRevealPinAccess';
import { usePlasticCard } from '../../../hooks/usePlasticCard';

type Props = {
  isBlocked: boolean;
  cardStatus: ApiCard['status'];
  isDisabled?: boolean;
};

const SFSRevealPinButton = ({ isBlocked, cardStatus, isDisabled }: Props) => {
  const hasSFSPinRevealAccess = useSFSRevealPinAccess();
  const card = usePlasticCard();
  const { t } = useTranslation('global');

  const [isLoading, setIsLoading] = useState(false);
  const [showOtpSetupModal, setShowOtpSetupModal] = useState(false);
  const [showScaModal, setShowScaModal] = useState(false);

  if (!hasSFSPinRevealAccess || isBlocked) {
    return null;
  }

  const isNonActivatedSfsCard =
    card.banking_provider === 'sfs' && cardStatus === 'PRE';

  const isButtonDisabled = isNonActivatedSfsCard;

  return (
    <>
      <IconButtonText
        isDisabled={isButtonDisabled || isDisabled || isLoading}
        onClick={async () => {
          try {
            setIsLoading(true);
            const isMfaActive = await getUserFactorsAndCheckIfPhoneActive();
            if (!isMfaActive) {
              setShowOtpSetupModal(true);
              return;
            }
            track(AnalyticEventName.CARD_SEE_PIN_REVEAL);
            setShowScaModal(true);
          } catch {
            logger.warn('Failed to reveal pin', {
              team: 'cards-ux',
              scope: 'card::sfs-pin-reveal',
            });
          } finally {
            setIsLoading(false);
          }
        }}
        text={t('cards.actions.seePinModal')}
        iconName="key"
      />
      {showOtpSetupModal && (
        <SfsUkPhoneNumberModal
          onClose={() => setShowOtpSetupModal(false)}
          isOpen={showOtpSetupModal}
        />
      )}
      {showScaModal && (
        <LazyRevealPinScaModal
          isOpen={showScaModal}
          onClose={() => {
            setShowScaModal(false);
          }}
        />
      )}
    </>
  );
};

const SFSRevealPinButtonWithErrorBoundary = withErrorBoundary({
  team: 'cards-ux',
  scope: 'card::marqeta-pin-reveal::modal',
})(SFSRevealPinButton);

export { SFSRevealPinButtonWithErrorBoundary as SFSRevealPinButton };
